import atlas, {source, layer, data} from "azure-maps-control"
import {GeomUtils} from "@esWebAPI/es/util/GeomUtils";

type Position = atlas.data.Position;
import type {SymbolLayerOptions, PolygonLayerOptions} from "azure-maps-control"
import type {Polygon, Point, MultiPoint} from "geojson";
import type {AzureBaseMap} from "./AzureBaseMap";
import {Feature} from "@esWebAPI/es/model/Feature";
import {feature} from "@turf/helpers"

export class AzureOverlay {

    private readonly DATASOURCE_ID = "overlay";
    private readonly HILITE_DATASOURCE_ID = "hilite-overlay";

    public static readonly SYMBOL_LAYER_ID = "overlay-symbol";
    public static readonly LINE_LAYER_ID = "overlay-line";
    public static readonly POLYGON_LAYER_ID = "overlay-polygon";
    public static readonly BUBBLE_LAYER_ID = "overlay-bubble";

    public static getLayerIds() {
        return [AzureOverlay.SYMBOL_LAYER_ID, AzureOverlay.LINE_LAYER_ID, AzureOverlay.POLYGON_LAYER_ID, AzureOverlay.BUBBLE_LAYER_ID];
    }

    private readonly baseMap: AzureBaseMap;

    private readonly symbolLayer: layer.SymbolLayer;
    private readonly lineLayer: layer.LineLayer;
    private readonly polygonLayer: layer.PolygonLayer;
    private readonly hiliteBubbleLayer: layer.BubbleLayer;

    /**
     * Get default server style for a polygon.
     * `es.util.color.getDefaultPolygonStyle( es.util.color.BLACK )`
     * `es.util.color.getDefaultPolygonStyle( es.util.color.hexToServer(es.util.color.palettes["Vivid"][0]) )`
     *
     * @param lineColor optional serverColor for polygon outline and fill (fill will be 50% transparent). default es.util.color.BLACK
     * @param fillTransparency optional fill transparency, default is 50
     */
    public static getDefaultPolygonStyle(lineColor: number | undefined, fillTransparency = 50): any {

        const rs = es.util.object.deepCopy({
            "$type": "PolygonStyle",
            "line": {
                "color": es.util.color.BLACK,
                "endCap": 1,
                "join": 1,
                "width": 2
            },
            "fillColor": es.util.color.BLACK_TRANSPARENT
        });

        if(lineColor){
            rs.line.color = lineColor;
            rs.fillColor = es.util.color.withTransparency(lineColor, fillTransparency);
        }

        return rs;
    }

    private sequence = 1;

    constructor(baseMap: AzureBaseMap) {
        this.baseMap = baseMap;
        const datasource = new source.DataSource(this.DATASOURCE_ID);
        const hiliteDatasource = new source.DataSource(this.HILITE_DATASOURCE_ID);

        this.symbolLayer = new layer.SymbolLayer(datasource.getId(), AzureOverlay.SYMBOL_LAYER_ID, {
            filter: ['in', ['geometry-type'], ['literal', ["Point", "MultiPoint"]]],
            iconOptions: {
                ignorePlacement: true, //To skip label collision detection for better performance.
                allowOverlap: true,    //To ensure smooth rendering when dragging, allow symbol to overlap all other symbols on the map.
                image: ['get', 'image'],
                anchor: "center"
            },
            textOptions: {
                allowOverlap: true,
                ignorePlacement: true,
                offset: ['case', ['has', 'textOffset'], ['get', 'textOffset'], ['literal', [0,0]]],
                haloBlur: ['case', ['has', 'haloBlur'], ['get', 'haloBlur'], ['literal', 0]],
                haloWidth: ['case', ['has', 'haloWidth'], ['get', 'haloWidth'], ['literal', 0]],
                haloColor: ['case', ['has', 'haloColor'], ['get', 'haloColor'], ['literal', "rgba(0,0,0,0)"]],
            }
        } as SymbolLayerOptions);

        this.lineLayer = new layer.LineLayer(datasource.getId(), AzureOverlay.LINE_LAYER_ID, {
            strokeColor: ['get', 'strokeColor'],
            strokeWidth: ['get', 'strokeWidth']
        });

        this.polygonLayer = new layer.PolygonLayer(datasource.getId(), AzureOverlay.POLYGON_LAYER_ID, {
            filter: ['==', ['geometry-type'], "Polygon"],
            fillColor: ['case', ['has', 'fillColor'], ['get', 'fillColor'], '#000000'], //TODO use stroke color as fallback
            fillOpacity:  ['case', ['has', 'fillOpacity'], ['get', 'fillOpacity'], 0.5]
        } as PolygonLayerOptions);

        this.hiliteBubbleLayer = new layer.BubbleLayer(hiliteDatasource.getId(), AzureOverlay.BUBBLE_LAYER_ID, {
            color: 'rgb(0, 204, 255)',
            strokeWidth: 0,
            pitchAlignment: 'map' // make pulse stay flat on the map when the map is pitched.
        });

        baseMap.map.events.add('load', () => {
            this.baseMap.map.sources.add([datasource, hiliteDatasource]);
            this.baseMap.map.layers.add([this.polygonLayer, this.lineLayer, this.symbolLayer, this.hiliteBubbleLayer]);

            //TOR9-19168 set textField afterward to prevent "use of "text-field" requires a style "glyphs" property" error
            this.symbolLayer.setOptions({textOptions: {textField: ['get', 'label']}});
        });
    }

    private getDatasource(id: string) {
        return this.baseMap.map.sources.getById(id) as source.DataSource;
    }

    public remove(overlayId: number) {
        if(!overlayId){
            es.log.warn("attempted to remove overlayId", overlayId);
            return;
        }
       this.getDatasource(this.DATASOURCE_ID).removeById(overlayId);
    }

    public removeAll(ids: number[]){
        ids.forEach(id => this.getDatasource(this.DATASOURCE_ID).remove(id));
    }

    public clear(){
        this.getDatasource(this.DATASOURCE_ID).clear();
    }

    private styleToProps(style: any, props: any) {
        if (style && (style.$type === "PolygonStyle" || style.$type === "LineStyle")) {
            if (style.line) {
                props.strokeColor = es.util.color.serverToHex(style.line.color);
                props.strokeWidth = style.line.width;
            }
            if (style.hasOwnProperty("fillColor")) {
                props.fillOpacity = Math.abs((es.util.color.getTransparency(style.fillColor) / 100) - 1);
                if (style.fillColor > 0) {
                    props.fillColor = es.util.color.serverToHex(style.fillColor);
                }
            }
        }

        if (style?.hasOwnProperty("text")) {
            const isLongLabel = props?.label?.length > 22; // a bit arbitrary as to calc real size would require font metrics but hopefully is usually right
            if (style.text.position === "BELOW") {
                props.textOffset = [0, isLongLabel ? 2 : 1.5]
                //props.anchor = "bottom";
            } else if (style.text.position === "ABOVE") {
                props.textOffset = [0, isLongLabel ? -2 : -1.5]
                //props.anchor = "top";
            }

            if(style.text?.fontOutlineColor){
                props.haloColor = es.util.color.getHex(style.text?.fontOutlineColor);
                props.haloWidth = 2.8;
                props.haloBlur = 1;
            } else {
                props.haloColor = "rgba(0,0,0,0)";
                props.haloWidth = 0;
                props.haloBlur = 0;
            }
        }
    }

    /**
     * Add a new feature to the overlay layer
     * @param geom Geometry
     * @param style Style to apply. When using symbol make sure to call {@link AzureImageSpriteManager.add} before adding an overlay.
     * @param {string} [label] Label text to display for Point only
     * @param {any} properties Set custom properties for the overlay feature
     * @return {number} Id of the add overlay feature
     */
    public add(geom: data.Geometry, style?: any, label?: string, properties?: any): number {
        //es.log.log("overlay add", geom, style, label, properties);
        if ((geom as any).type === "Feature") {
            // a GeoJSON Feature passed in here would get nested inside another Feature and silently render nothing (TOR9-19986)
            es.log.warn("AzureOverlay.add called with a Feature instead of a Geometry, unwrapping", geom);
            geom = (geom as any).geometry;
        }
        const id = this.sequence++;

        let props: any = {
            label: label,
        };

        // TODO: handle other types
        if(["Polygon", "MultiPolygon"].includes(geom.type)) {
            if(!style){
                style = AzureOverlay.getDefaultPolygonStyle();
            }
        } else if (["Point", "MultiPoint"].includes(geom.type)){
            props.image = this.getIconImageId(style);
        }

        if (properties) {
            props = {...props, ...properties};
        }

        //Pass the style options in the feat properties, they will be read by the expression defined in the layers options
        this.styleToProps(style, props);

        const feat = new data.Feature<data.Geometry,any>(geom, props, id);
        this.getDatasource(this.DATASOURCE_ID).add(feat);
        //es.log.log("overlay#add", feat);
        return id;
    }

    public hasOverlayId(overlayId: number){
        const shape = this.getDatasource(this.DATASOURCE_ID).getShapeById(overlayId);
        return !!shape;
    }

    /**
     * Update the geometry of an overlay feature
     * @param {number} overlayId Id of the overlay feature
     * @param {data.Geometry} geom New geometry
     * @param {string} label New label
     * @param {any} style New style
     */
    public update(overlayId: number, geom?: data.Geometry, label?: string, style?: any): void {

        const shape = this.getDatasource(this.DATASOURCE_ID).getShapeById(overlayId);
        if(!shape){
            es.log.warn(`Could not get shape with id ${overlayId} from ds ${this.DATASOURCE_ID}`, geom, label, style);
            return;
        }
        const props = shape.getProperties();
        const type = shape.getType();

        if (style) {
            this.styleToProps(style, props);
        }

        if (geom && (geom?.type === "MultiPolygon" && type === "Polygon") || (geom?.type === "Polygon" && type === "MultiPolygon") ||
                    (geom?.type === "MultiLineString" && type === "LineString") || (geom?.type === "LineString" && type === "MultiLineString")) {
            // setCoordinates is not working when switching between MultiPolygons and Polygons, the type is not updated in Azure source code
            // e = "Polygon" === this.getType() && 1 === Position._getDimensions(e) ? [e] : e;
            // isEqual$1(this.data.geometry.coordinates, e) || (this.data.geometry.coordinates = cloneDeep$1(e),

            this.getDatasource(this.DATASOURCE_ID).removeById(overlayId);
            const newFeat = new data.Feature<data.Geometry,any>(geom, props, shape.getId());
            this.getDatasource(this.DATASOURCE_ID).add(newFeat);
        }

        else if (geom) {
            this.getDatasource(this.DATASOURCE_ID).getShapeById(overlayId).setCoordinates(geom.coordinates);
        }

        if (label) {
            props.label = label;
        }
        if (style) {
            props.image = this.getIconImageId(style);
        }
        this.getDatasource(this.DATASOURCE_ID).getShapeById(overlayId).setProperties(props);
    }

    private getIconImageId(style: any): string|undefined {
        if (style?.$type === "TextOnlyStyle") {
            return undefined;
        }
        if (style?.$type === "SymbolStyle") {
            return `es_${style.symbolTypeId}_${style.size}_${style.color}`;
        }
        return 'pin-round-darkblue';
    }

    /**
     * Draw lonlat [min.lng, min.lat, max.lng, max.lat] bounds
     * e.g.
     *     esApp.map.overlay.drawBounds([-6.4277271, 53.3693705, -6.0575425, 53.6186746])
     * @param bounds [min.lng, min.lat, max.lng, max.lat]
     * @param style
     * @param clear auto clear the bounds after this many ms. -1 (default) is never.
     */
    public drawBounds(bounds: number[], style: any, clear = -1) {
        const poly = new atlas.data.Polygon([
            [bounds[0],bounds[3]],
            [bounds[2],bounds[3]],
            [bounds[2],bounds[1]],
            [bounds[0],bounds[1]],
            [bounds[0],bounds[3]]
        ] as Position[]);

        const overlayId = this.add(poly, style);

        if(clear > 0){
            window.setTimeout(() => {
                this.remove(overlayId)
            }, clear);
        }
    }

    /**
     * Pulse a point on the map.
     * Note: this overlay is an independent of any other overlays in this module and has its own layer and datasource.
     * e.g.
     *     esApp.map.overlay.pulsePoint([-6.4277271, 53.3693705])
     * @param lonLat
     * @param showSymbol show an overlay symbol as well as the pulse (default false)
     * @param autoClear auto clear the bounds after this many ms. -1 (default) is never.
     * @see: https://samples.azuremaps.com/animations/pulse-animation-with-bubble-layer
     */
    public pulsePoint(lonLat: Position | [number, number], showSymbol = false, autoClear = 10000) {
        if(!this.getDatasource(this.HILITE_DATASOURCE_ID)){
            console.warn("pulsePoint called before init for point", lonLat);
            return;
        }
        if(!Array.isArray(lonLat) || isNaN(lonLat[0]) || isNaN(lonLat[1])){
            console.warn("pulsePoint called with invalid lonLat", lonLat);
            return;
        }
        const id = this.sequence++;
        const duration = 2600; // duration for the animation
        const maxRadius = 40;  // max radius of the pulse circle
        
        const point = new atlas.data.Point(lonLat as Position);
        const feat = new data.Feature<data.Geometry, any>(point, undefined, id);

        this.getDatasource(this.HILITE_DATASOURCE_ID).add(feat);

        let symbolId = -1;
        if(showSymbol){
            symbolId = this.add(point /*, es.util.mapScope.defaultAddressPointStyle*/);
        }

        const hbl = this.hiliteBubbleLayer; // fixes the "this" ref used below
        let start: number | null = null;

        function animate(timestamp: number) {
            if (!start) start = timestamp;
            const progress = (timestamp - start) / duration;

            if (progress < 1) {
                const currentRadius = maxRadius * progress;
                const currentOpacity = Math.max(0.9 - progress, 0);
                hbl.setOptions({
                    radius: currentRadius,
                    opacity: currentOpacity
                });
                requestAnimationFrame(animate);
            } else {
                // Reset start time for a continuous loop
                start = timestamp;
                requestAnimationFrame(animate);
            }
        }

        requestAnimationFrame(animate);

        if(autoClear){
            const destroy = () => {
                this.getDatasource(this.HILITE_DATASOURCE_ID).removeById(id);
                if(showSymbol){
                    this.remove(symbolId);
                }
            };
            window.setTimeout(destroy, autoClear);
        }
    }

    /**
     * Display a geom onscreen for a period of time.
     * @param geom (Point | MultiPoint | LineString | MultiLineString | Polygon | MultiPolygon)
     * @param style 3 options:
     *              1) custom
     *              2) undefined for eS default (turqoise)
     *              3) "azure" for azure default (black, 50% opacity)
     *              4) hex color value e.g. "#F0F0F0"
     *              5) number server color value e.g. 4278242559
     * @param autoClear
     */
    public pulseGeom(geom: data.Geometry, style?: any, autoClear = 3000) {
        if(!this.getDatasource(this.HILITE_DATASOURCE_ID)){
            // user likely mouseover results table or something before datasource has init. log a warn msg and ignore
            console.warn("pulsePolygon called before init for geom", geom);
            return;
        }

        const defaultStyle = {
            "$type": "PolygonStyle",
            "line": {
                "color": es.util.color.HILITE_COLOR_TURQUOISE,
                "endCap": 1,
                "join": 1,
                "width": 4
            },
            "fillColor": 0x80FFFFFF,
        };
        // or reuse the same MBR styling terAssist draws bounds with? (width 2 black outline and black fill at 80% transparency.)
        //const defaultStyle = AzureOverlay.getDefaultPolygonStyle(es.util.color.BLACK, 80);


        if (style === "azure") {
            style = undefined;
        } else if (typeof style === "number") {
            defaultStyle.line.color = style;
            style = defaultStyle;
        } else if (typeof style === "string") {
            defaultStyle.line.color = es.util.color.serverFromHex(style);
            style = defaultStyle;
        } else {
            style = style || defaultStyle;
        }

        const overlayId = this.add(geom, style);

        if(autoClear) {
            setTimeout(() => {
                this.remove(overlayId)
            }, autoClear);
        }
    }

    /**
     * Highlight/hilite a point|polygon|multipolygon on the map
     * @param serverGeom lonLat e.g. ['PT', -86.82708, 33.4879]
     */
    public hilite(serverGeom: any) {
        if(serverGeom[0] == "PT") {
            const lonLat = [serverGeom[1], serverGeom[2]];
            this.pulsePoint(lonLat, false, 2500);
        } else if(serverGeom[0] == "PN" || serverGeom[0] == "MP"){
            const geoJsonGeom = GeomUtils.serverToGeoJson(serverGeom) as Polygon;
            this.pulseGeom(geoJsonGeom, undefined, 2500);
        } else if(serverGeom[0] == "MG"){
            const geoJsonPoint = GeomUtils.serverToGeoJson(serverGeom[1]) as Point;
            const geoJsonPoly = GeomUtils.serverToGeoJson(serverGeom[2]) as Polygon;
            this.pulsePoint(geoJsonPoint.coordinates, undefined, 2500);
            this.pulseGeom(geoJsonPoly, undefined, 2500);
        } else if(serverGeom[0] == "MT"){
            const geoJsonGeom = GeomUtils.serverToGeoJson(serverGeom) as MultiPoint;
            const coordinates = geoJsonGeom.coordinates.slice(0,100);
            for(const lonLat of coordinates){
                this.pulsePoint(lonLat as Position, false, 2500);
            }
        }  else {
            console.warn("hilite called with unsupported geom type", serverGeom);
        }
    }

    /**
     * Hilite and optionally navigate to points by featKey.
     * @example
     *    esApp.map.overlay.hilitePointByFeatKey(["188_1", "188_2","188_3"]);
     *    ~~esApp.map.overlay.hilitePointByFeatKey("188_1,188_2,188_3");~~ no longer supported. instead, first call: "188_1,188_2,188_3".split(",").map(fk => fk.trim())
     * @param featKeys array as above
     * @param zoomToPoint navigate to point or not (default false)
     */
    public hilitePointByFeatKey(featKeys: string[], zoomToPoint = true) {
        es.api.feature.getByFeatKeys(featKeys).then((feats: Feature[]) => {
            for (const feat of feats) {
                if (!feat.geom || !feat.geom[0]) {
                    console.warn("hilitePointByFeatKey: feature has no geometry", feat);
                    continue;
                }

                if (zoomToPoint) {
                    let point;
                    if (feat.geom[0] == "PT") {
                        point = feat.geom.slice(1,3);
                    }
                    else if (feat.geom[0] == "PN" || feat.geom[0] == "MP"){
                        const geoJsonGeom = GeomUtils.serverToGeoJson(feat.geom) as Polygon;
                        point = GeomUtils.getPolygonVisualCenter(feature(geoJsonGeom));
                    }
                    if (!point) {
                        console.warn("hilitePointByFeatKey: unsupported geom type: " + feat.geom[0], feat);
                        continue;
                    }

                    this.baseMap.setCenterAndResolution(point[0], point[1], 3);
                    setTimeout(() => {
                        this.hilite(feat.geom);
                    }, 1000);
                } else {
                    this.hilite(feat.geom);
                }
            }
        }).fail((args: any) => {
            console.log("hilitePointByFeatKey err", args);
        });
    }
}
